import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Tooltip from '../../../components/Tooltip'
import type { HrcConnectedSystem } from '../types'

const HEADING_ID = 'hrc-connected-systems-heading'

/** Bound to the Helix feedback tokens, so both pills follow the design system's own light and
 *  dark values instead of hand-picked palette steps. */
const PILL: Record<HrcConnectedSystem['state'], { id: string; className: string }> = {
  ACTIVE: {
    id: 'healthRecords.connectedSystems.stateActive',
    className: 'bg-success-100 text-success-600',
  },
  EXPIRED: {
    id: 'healthRecords.connectedSystems.stateExpired',
    className: 'bg-warning-100 text-warning-600',
  },
}

type Props = {
  systems: HrcConnectedSystem[]
}

/** A circled "i": the cue that the badge it sits in explains itself on hover or tap. Decorative
 *  (the badge text and its tooltip description carry the meaning), so hidden from AT. It draws in
 *  `currentColor`, so it follows the badge's Helix text token in light and dark mode. */
const InfoIcon: React.FC = () => (
  <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.5a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9 9a.75.75 0 0 0 0 1.5h.25v3a.75.75 0 0 0 1.5 0V9.75A.75.75 0 0 0 10 9H9Z"
      clipRule="evenodd"
    />
  </svg>
)

/**
 * The health systems this subject has already connected, listed above the Share Records
 * button so the family can see what sharing is in place before adding more.
 *
 * A WIDER set than the connection count: that number says whether they are sharing right
 * now, this says what they have connected, so an expired entry stays and is labelled.
 * Someone who connected a hospital last year should see it lapsed rather than see nothing
 * and wonder whether it ever worked.
 *
 * Nothing connected renders nothing at all — a bare header over an empty list is a worse
 * answer than letting the Share Records card speak for itself.
 */
const HrcConnectedSystems: React.FC<Props> = ({ systems }) => {
  const intl = useIntl()

  if (systems.length === 0) return null

  return (
    <section aria-labelledby={HEADING_ID} className="flex flex-col gap-2">
      <h3 id={HEADING_ID} className="text-text-heading text-sm font-semibold dark:text-white">
        <FormattedMessage
          id="healthRecords.connectedSystems.heading"
          defaultMessage="Health systems you have connected"
        />
      </h3>
      <ul className="flex flex-col">
        {systems.map((system, index) => {
          const name = system.name?.trim()
          // `platformType` is the EHR vendor — Epic, Cerner — NOT the health system, so it is
          // supporting text rather than the row's name. A whole message with a placeholder,
          // not concatenation, so a translator can reorder it around the vendor.
          const platform = system.platformType?.trim()
          const pill = PILL[system.state]
          return (
            <li
              // No stable id: the response deliberately carries no connection id, and
              // these rows hold no state of their own, so position is a safe key.
              key={`${system.connectedAt ?? ''}-${name ?? ''}-${index}`}
              className="border-border-base flex items-center justify-between gap-4 border-b py-3 last:border-b-0 dark:border-gray-700"
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-text-heading text-sm font-medium dark:text-white">
                  {name ||
                    intl.formatMessage({
                      id: 'healthRecords.connectedSystems.unnamed',
                      defaultMessage: 'Health system',
                    })}
                </span>
                {platform ? (
                  <span className="text-text-body text-xs dark:text-gray-400">
                    <FormattedMessage
                      id="healthRecords.connectedSystems.via"
                      defaultMessage="Connected through {platform}"
                      values={{ platform: `${platform.charAt(0).toUpperCase()}${platform.slice(1)}` }}
                    />
                  </span>
                ) : null}
              </span>
              {/* One status per row: a failed verification REPLACES Active/Expired. The row may
                  still read ACTIVE in HRC (only a superseded failure is revoked), but it no
                  longer exports, so "Active" beside the failure would contradict it. */}
              <span className="flex shrink-0 items-center gap-2">
                {system.verificationFailed ? (
                  <Tooltip
                    content={intl.formatMessage({
                      id: 'healthRecords.connectedSystems.verificationFailedHint',
                      defaultMessage:
                        'The connection failed the identity verification. Please reconnect with the correct patient account.',
                    })}
                  >
                    <span className="bg-error-100 text-error-600 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
                      <InfoIcon />
                      <FormattedMessage
                        id="healthRecords.connectedSystems.verificationFailed"
                        defaultMessage="Verification failed"
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${pill.className}`}>
                    <FormattedMessage id={pill.id} />
                  </span>
                )}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default HrcConnectedSystems
